import { partTypeApi, procurementApi } from '@/main/api';
import { KeyValueDefault } from '@/main/models/common';
import { GetReassignItemsFilterRequest } from '@/main/models/request';
import {
  GetQuotePartTypesByDirectAssignCriteriaResponse,
  ReassignProcurementHeaderResponse
} from '@/main/models/response';
import { isNullUndefined } from '@/main/services/reducerUtils';
import { CancelledPartHeaderInfo } from '@/main/services/storage';
import { isNullUndefinedEmpty } from '@/main/services/utils';
import { ReassignPartData } from './reassignScreenUtils';

async function loadPartTypes(
  quoteGuids: string[],
  guidClaim: string | undefined
): Promise<GetQuotePartTypesByDirectAssignCriteriaResponse[]> {
  if (isNullUndefined(quoteGuids) || quoteGuids.length === 0) {
     try {
      const res =
        await partTypeApi.getPartTypeByDirectAssignCriteria(
          guidClaim, undefined, undefined
        );
      return [{
        guidQuote: "",
        partTypes: res.data.result ?? []
      }];
    } catch {
      return [];
    }
  }
  try {
    const res =
      await partTypeApi.getPartTypeByDirectAssignCriteriaCancelledParts(
        quoteGuids
      );
    return res.data.result ?? [];
  } catch {
    return [];
  }
}

function buildPartTypeOptionsByQuote(
  options: GetQuotePartTypesByDirectAssignCriteriaResponse[]
): Record<string, KeyValueDefault[]> {
  const map: Record<string, KeyValueDefault[]> = {};
  options.forEach(item => {
    map[item.guidQuote] = item.partTypes;
  });
  return map;
}

function buildAggregatedPartTypeOptions(
  options: GetQuotePartTypesByDirectAssignCriteriaResponse[]
): Record<string, KeyValueDefault[]> {
  const map = new Map<string, KeyValueDefault>();
  options.forEach(item => {
    item.partTypes.forEach(opt => {
      const key = String(opt.default);
      if (!map.has(key)) map.set(key, opt);
    });
  });
  return { '': Array.from(map.values()) };
}

function mergeCancelledInfoIntoItems(
  items: ReassignPartData[],
  info: CancelledPartHeaderInfo[]
): ReassignPartData[] {
  const byItem = new Map(
    info.map(i => [i.procurementSupplierItemGuid, i] as const)
  );
  return items.map(item => {
    const match = byItem.get(item.guidOriginalProcurementSupplierItem);
    if (!match) return item;
    return {
      ...item,
      claimNumber: item.claimNumber ?? match.claimNumber,
      quoteCode: item.quoteCode ?? match.quoteCode,
      procurementCode: item.procurementCode ?? match.procurementCode,
      insurerName: item.insurerName ?? match.insurerName,
      vehicleFullName: item.vehicleFullName ?? match.vehicleFullName
    };
  });
}

function mergeHeaderIntoItems(
  items: ReassignPartData[],
  header: ReassignProcurementHeaderResponse
): ReassignPartData[] {
  return items.map(item => ({
    ...item,
    quoteGuid: item.quoteGuid ?? header.guidQuote,
    claimNumber: item.claimNumber ?? header.claimNumber,
    quoteCode: item.quoteCode ?? String(header.quoteCode),
    procurementCode: item.procurementCode ?? String(header.procurementCode),
    insurerName: item.insurerName ?? header.insurerName,
    vehicleFullName: item.vehicleFullName ?? header.vehicleFullName
  }));
}

export async function loadReassign(
  request: GetReassignItemsFilterRequest,
  guidProcurementHeader: string | null,
  guidClaim: string | null,
  cancelledQuoteGuids?: string[],
  cancelledPartsInfo?: CancelledPartHeaderInfo[]
): Promise<{
  parts: ReassignPartData[];
  guidCompanyBuyer?: string;
  vehicleFullName?: string;
  possibleTotalLoss?: boolean;
  partTypeOptionsByQuote: Record<string, KeyValueDefault[]>;
}> {;
  const [itemsRes, headerRes] = await Promise.all([
    procurementApi.getReassignItems(request),
    guidProcurementHeader ?? guidClaim
      ? procurementApi.getReassignHeader(guidProcurementHeader, guidClaim)
      : null
  ]);

  const result = itemsRes.data.result;

  const items: ReassignPartData[] = Array.isArray(result)
    ? result
    : result?.items ?? [];
  const guidCompanyBuyer = Array.isArray(result)
    ? undefined
    : result?.guidCompanyBuyer;
  const resultVehicleFullName = Array.isArray(result)
    ? undefined
    : result?.vehicleFullName;

  if (items.length === 0) {
    throw new Error('quoteNotFound');
  }

  const header = headerRes?.data.result ?? null;
  const mergedWithHeader = header
    ? mergeHeaderIntoItems(items, header)
    : items;
  const parts = cancelledPartsInfo?.length
    ? mergeCancelledInfoIntoItems(mergedWithHeader, cancelledPartsInfo)
    : mergedWithHeader;

  const isCancelledMode = Boolean(cancelledQuoteGuids?.length);
  const quoteGuids = isCancelledMode
    ? (cancelledQuoteGuids as string[])
    : Array.from(
        new Set(
          parts
            .map(p => p.quoteGuid)
            .filter((guid): guid is string => !isNullUndefinedEmpty(guid))
        )
      );
  
  const partTypes = await loadPartTypes(quoteGuids, request.guidClaim ?? undefined);
  const partTypeOptionsByQuote = isCancelledMode
    ? buildAggregatedPartTypeOptions(partTypes)
    : buildPartTypeOptionsByQuote(partTypes);

  return {
    parts,
    guidCompanyBuyer: guidCompanyBuyer || header?.guidCompanyBuyer || undefined,
    vehicleFullName:
      header?.vehicleFullName || resultVehicleFullName || undefined,
    possibleTotalLoss: header?.possibleTotalLoss,
    partTypeOptionsByQuote
  };
}
